import gsap from "gsap";
import { DateTime } from "luxon";
import className from "classnames/bind";
import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { replaceStringVar } from "@/core/utils";
import { useTicketing } from "../TicketingProvider";
import { MaterialLoader } from "@/components/MaterialLoader";
import { RadioButton } from "@/components/FormComponents";
import { useViewSize } from "@/providers/ViewSizeContext";
import { useClientMount } from "@/providers/ClientMountProvider";

import styles from "./TicketingForm.module.scss";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
const cx = className.bind(styles);


type SessionButtonProps = {
  onClick: (value: string) => void;
  session: TicketureSessionTime;
  active: boolean;
}

const SessionButton = ({ onClick, session, active } : SessionButtonProps) => {
  const sessionTime = DateTime.fromISO(session.start_datetime).setZone("America/New_York").toLocaleString(DateTime.TIME_SIMPLE);
  const handleClick = () => onClick(session.id);
  return (
    <RadioButton
      className={cx("session-button")}
      active={active}
      label={sessionTime}
      value={session.id}
      onClick={handleClick}
    />
  );
}

export default function StepTwo() {
  const {
    timesLoading,
    sessions,
    lastSearchDate,
    selectedSession,
    setSelectedSession,
    infoMessage,
    data: { step2Label, step2Subtext, infoEyebrow }
  } = useTicketing();
  const { mounted } = useClientMount();
  const { viewHeight, isReady } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const showTimes = !timesLoading && lastSearchDate;
  const noTimes = !timesLoading && sessions.length === 0 && lastSearchDate;
  const parsedLastSearchDate = lastSearchDate ? DateTime.fromISO(lastSearchDate).toFormat('MMMM dd, yyyy') : null;
  const infoBoxEyebrow = replaceStringVar(infoEyebrow, { date: parsedLastSearchDate || '' });
  const scrollToStep = (anim: { opacity: 0 | 1 }) => {
    if (!mounted || !isReady) return;
    if (!rootEl.current || !showTimes || anim.opacity === 0) return;
    gsap.to(window, { duration: 0.5, scrollTo: { y: rootEl.current, offsetY: (viewHeight - rootEl.current.offsetHeight) / 2 } });
  }
  useEffect(() => {
    if (!mounted || !isReady) return;

    gsap.registerPlugin(ScrollToPlugin);
  }, [mounted, isReady]);
  return (
    <div className={cx(["step", "step-two"])} ref={rootEl}>
      <div className={cx("step__copy")}>
        <p className={cx(["step-label", "text-l"])}>{step2Label}</p>
        <p className={cx(["step-desc", "text-m"])}>{step2Subtext}</p>
      </div>
      <AnimatePresence mode="wait">
        { timesLoading && (
          <MaterialLoader
            className={cx("step-two__loader")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="loader"
          />
        )}
        { showTimes && (
          <motion.div
            className={cx("step-two__session-info")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onAnimationStart={scrollToStep}
          >
            {
              infoMessage && (
                <div className={cx("step-two__info-message")}>
                  <p className="eyebrow-s">{infoBoxEyebrow}</p>
                  <div className="text-m" dangerouslySetInnerHTML={{ __html: infoMessage }} />
                </div>
              )
            }
            { noTimes ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={`no-times-${lastSearchDate}`}
                  className={cx("step-two__no-times")}
                >
                  <p className="text-m">No available times for {parsedLastSearchDate}.<br />Try another date.</p>
                </motion.div>
              ) : (
                <div className={cx("step-two__session-buttons")}>
                  { sessions?.map((session) => (
                    <SessionButton
                      key={session.id}
                      session={session}
                      active={session.id === selectedSession}
                      onClick={setSelectedSession}
                    />
                  ))}
                </div>
              )
            }
          </motion.div>
        )
      }
      </AnimatePresence>
    </div>
  );
}
